import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DollarSign, CheckCircle2, Clock, ExternalLink, Download } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Payouts() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });
  const { data, isLoading } = useQuery({ queryKey: ["payouts"], queryFn: async () => (await api.get("/payouts")).data });

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (range.start) params.set("start", range.start);
      if (range.end) params.set("end", range.end);
      const res = await api.get(`/payroll/commission-export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `commissions_${range.start || "all"}_${range.end || "all"}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Commission CSV exported");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Export failed"); }
  };

  const mark = async (row, status) => {
    setBusy(row.job_id);
    try {
      await api.patch(`/jobs/${row.job_id}/payout`, { amount: row.amount, status });
      toast.success(status === "paid" ? "Marked paid" : "Marked pending");
      qc.invalidateQueries({ queryKey: ["payouts"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(null);
  };

  if (isLoading) return <div className="p-10"><Spinner /></div>;
  const rows = data?.rows || [];

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="payouts-page">
      <Eyebrow>Subcontractor finance</Eyebrow>
      <h1 className="mt-2 font-display text-3xl font-bold text-white">Sub payouts</h1>
      <p className="mt-2 text-sm text-slate-400">Track and settle subcontractor payouts across every job. Marking a payout paid logs the date.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="card-tac p-5">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/10 text-amber-400"><Clock className="h-5 w-5" /></div>
          <div className="mt-3 font-display text-3xl font-black text-white" data-testid="payouts-pending-total">{money(data?.pending_total)}</div>
          <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Pending payout</div>
        </div>
        <div className="card-tac p-5">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-400"><CheckCircle2 className="h-5 w-5" /></div>
          <div className="mt-3 font-display text-3xl font-black text-white" data-testid="payouts-paid-total">{money(data?.paid_total)}</div>
          <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">Paid to date</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-2 rounded-xl border border-slate-800 bg-[#0d1420] p-4" data-testid="commission-export">
        <div className="flex items-center gap-1.5 font-mono-tac text-[10px] uppercase tracking-widest text-slate-500"><Download className="h-3.5 w-3.5 text-amber-400" /> Payroll export</div>
        <label className="text-xs text-slate-400">From <input data-testid="export-start" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="ml-1 rounded border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white" /></label>
        <label className="text-xs text-slate-400">To <input data-testid="export-end" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="ml-1 rounded border border-slate-700 bg-[#0b0f17] px-2 py-1 text-white" /></label>
        <button data-testid="export-csv-btn" onClick={exportCsv} className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs"><Download className="h-3.5 w-3.5" /> Export commissions CSV</button>
      </div>

      <div className="mt-6 space-y-3">
        {rows.map((r) => (
          <div key={r.job_id} data-testid={`payout-row-${r.job_id}`} className="card-tac flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-white">
                <Link to={`/jobs/${r.job_id}`} className="truncate hover:text-cyan-300">{r.title}</Link>
                <Link to={`/jobs/${r.job_id}`} className="text-slate-500 hover:text-cyan-300"><ExternalLink className="h-3.5 w-3.5" /></Link>
              </div>
              <div className="text-xs text-slate-400">{r.customer_name || "No customer"}{r.subs?.length ? ` · ${r.subs.join(", ")}` : ""}</div>
              {r.paid_at && <div className="mt-0.5 font-mono-tac text-[10px] text-emerald-400">Paid {String(r.paid_at).slice(0, 10)}</div>}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-display text-lg font-black text-white">{money(r.amount)}</span>
              {r.payout_status === "paid" ? (
                <button data-testid={`payout-unpay-${r.job_id}`} disabled={busy === r.job_id} onClick={() => mark(r, "pending")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/50 px-3 py-1.5 text-xs text-emerald-300 hover:border-emerald-400 disabled:opacity-50">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Paid
                </button>
              ) : (
                <button data-testid={`payout-pay-${r.job_id}`} disabled={busy === r.job_id} onClick={() => mark(r, "paid")}
                  className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs disabled:opacity-50">
                  <DollarSign className="h-3.5 w-3.5" /> Mark paid
                </button>
              )}
            </div>
          </div>
        ))}
        {!rows.length && <div className="card-tac py-12 text-center text-slate-500" data-testid="payouts-empty">No payouts recorded yet. Set a payout amount on a job to track it here.</div>}
      </div>
    </div>
  );
}
